import * as React from "react";

import useViewportPosition, {
  ViewportPosition,
} from "../../hooks/useViewportPosition";

import useUserInteraction, { Interaction } from "./useUserInteraction";

export interface StickyFormState {
  isHidden: boolean;
  isMouseIn?: boolean;
  isFocused?: boolean;
  viewportState?: boolean;
}

const reducer = (
  state: StickyFormState,
  action: ViewportPosition | Interaction
) => {
  const newState: StickyFormState = { ...state };
  switch (action) {
    case ViewportPosition.InViewport: {
      newState.viewportState = false;
      break;
    }
    case ViewportPosition.OutsideViewport: {
      newState.viewportState = true;
      break;
    }
    case Interaction.MouseEnter: {
      newState.isMouseIn = true;
      break;
    }
    case Interaction.MouseOut: {
      newState.isMouseIn = false;
      break;
    }
    case Interaction.FocusIn: {
      newState.isFocused = true;
      break;
    }
    case Interaction.FocusOut: {
      newState.isFocused = false;
      break;
    }
    default: {
      break;
    }
  }
  // Either the user is interacting and we don't want to hide it
  // OR the scroll & viewport conditions are met.
  newState.isHidden = !(
    newState.isFocused ||
    newState.isMouseIn ||
    newState.viewportState
  );
  return newState;
};

export default function useStickyProductForm() {
  const [state, dispatch] = React.useReducer(reducer, {
    isHidden: true,
  });

  // plug in custom hooks
  const { referenceElementProps } = useViewportPosition(dispatch);
  const { interactionProps } = useUserInteraction(dispatch);

  const stickyFormProps = React.useMemo(
    () => ({
      ...interactionProps,
      "aria-hidden": state.isHidden,
    }),
    [state, interactionProps]
  );

  return {
    stickyFormProps,
    referenceElementProps,
    ...state,
  };
}
